import React from "react";
import { TASK_LABEL } from "./taskCopy";
import { derivePlanningWorkflows, planningCoverageLabel } from "./planningState";

/**
 * Live preview of the same Task state the guided flow already holds.
 * Not a second editor — read-only rendering of current draft/readiness.
 */
function promiseItem(value) {
  if (typeof value === "string") return { text: value, fromAnswer: false };
  return {
    text: String(value?.text || "").trim(),
    fromAnswer: Boolean(value?.fromAnswer),
  };
}

function questionItem(value) {
  if (typeof value === "string") return { text: value, revealedByAnswer: false };
  return {
    text: value?.text || "",
    revealedByAnswer: Boolean(value?.revealedByAnswer),
  };
}

function decisionItem(value) {
  if (!value || typeof value === "string") return null;
  const question = String(value.question || "").trim();
  if (!question) return null;
  const status = value.status || "answered";
  const statusLabel =
    status === "not_required"
      ? "Does not apply"
      : status === "deferred"
        ? "Deferred"
        : "Decided";
  return {
    question,
    answer: String(value.answer || "").trim(),
    statusLabel,
  };
}

export default function TaskDocumentPanel({
  requestText,
  promises = [],
  openQuestions = [],
  decisions = [],
  readinessLines = [],
  warningLines = [],
  requirementBlock = null,
  agreementStatus = null,
}) {
  const questionItems = (openQuestions || []).map(questionItem).filter((item) => item.text);
  const promiseItems = (promises || []).map(promiseItem).filter((item) => item.text);
  const decisionItems = (decisions || []).map(decisionItem).filter(Boolean);
  const workflows = derivePlanningWorkflows({
    requestText,
    questions: [
      ...(decisions || []).map((item) => ({
        text: item?.question || item?.text,
        status: item?.status,
        answer: item?.answer,
      })),
      ...questionItems.map((item) => ({ text: item.text, status: "open" })),
    ],
  });
  return (
    <section className="task-document-panel" data-testid="task-document-panel">
      <h4>{TASK_LABEL} draft</h4>
      <div className="task-document-block">
        <h5>Request</h5>
        <p data-testid="task-document-request">
          {requestText || `No ${TASK_LABEL.toLowerCase()} request yet.`}
        </p>
      </div>
      <div className="task-document-block">
        <h5>Requirements</h5>
        {promiseItems.length === 0 ? (
          <p className="task-document-empty" data-testid="task-document-promises-empty">
            Requirements appear here as the conversation develops.
          </p>
        ) : (
          <ul data-testid="task-document-promises">
            {promiseItems.map((item) => (
              <li key={item.text}>
                {item.text}
                {item.fromAnswer ? (
                  <span className="task-document-changed" data-testid="task-document-requirement-from-answer">
                    {" "}
                    Updated from your answer
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="task-document-block">
        <h5>Decisions so far</h5>
        {decisionItems.length === 0 ? (
          <p className="task-document-empty" data-testid="task-document-decisions-empty">
            Answers appear here as you decide them.
          </p>
        ) : (
          <ul data-testid="task-document-decisions">
            {decisionItems.map((item) => (
              <li key={item.question}>
                <strong>{item.statusLabel}:</strong> {item.question}
                {item.answer ? ` — ${item.answer}` : ""}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="task-document-block">
        <h5>Open questions</h5>
        {questionItems.length === 0 ? (
          <p className="task-document-empty">No open product questions.</p>
        ) : (
          <ul data-testid="task-document-open-questions">
            {questionItems.map((item) => (
              <li key={item.text}>
                {item.text}
                {item.revealedByAnswer ? (
                  <span className="task-document-changed" data-testid="task-document-question-revealed">
                    {" "}
                    New after your last answer
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
      {workflows.length > 0 ? (
        <details className="task-document-block" data-testid="task-document-workflows">
          <summary>Workflows and edge cases</summary>
          <ul>
            {workflows.map((item) => (
              <li key={item.id} data-coverage={item.coverage}>
                <strong>{planningCoverageLabel(item.coverage)}:</strong> {item.title}
                {item.summary ? ` — ${item.summary}` : ""}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      {requirementBlock ? (
        <div
          className="task-document-block"
          data-testid="task-document-contradiction"
          role="status"
        >
          <h5>Contradiction</h5>
          <p>
            {requirementBlock.headline ||
              requirementBlock.summary ||
              "These requirements conflict and must be settled before approval."}
          </p>
        </div>
      ) : null}
      <div className="task-document-block">
        <h5>Readiness</h5>
        {agreementStatus?.label ? (
          <p data-testid="task-document-agreement">{agreementStatus.label}</p>
        ) : null}
        {readinessLines.length > 0 ? (
          <ul data-testid="task-document-readiness">
            {readinessLines.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        ) : (
          <p className="task-document-empty">No extra readiness blockers.</p>
        )}
        {warningLines.length > 0 ? (
          <ul data-testid="task-document-warnings">
            {warningLines.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  );
}
